import React, { useState } from 'react';
import { ArrowLeftIcon, BriefcaseIcon, CheckIcon, DownloadIcon, EyeIcon, PlusIcon, SaveIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { CompanySettings, Material, ProCustomer, Quote, QuoteLine, QuoteStatus } from '../../types/pro';
import { QUOTE_STATUS, emptyQuoteLine, formatDate, kr, krPrecise, lineTotal, newLineId, toISODate, totals } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
import { SendDocumentDialog } from '../../components/pro/SendDocumentDialog';
import { primaryContact } from '../../utils/contacts';
interface ProQuoteEditorProps {
  quote: Quote;
  customers: ProCustomer[];
  materials: Material[];
  company: CompanySettings;
  onBack: () => void;
  onChange: (changes: Partial<Quote>) => void;
  onCreateJob: (quote: Quote) => void;
}
export function ProQuoteEditor({
  quote,
  customers,
  materials,
  company,
  onBack,
  onChange,
  onCreateJob
}: ProQuoteEditorProps) {
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const sums = totals(quote.lines, company.vatRate);

  // Every field already writes through on change; this just confirms it.
  const saveDraft = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };
  const quoteCustomer = customers.find(item => item.id === quote.customerId);
  const quoteContact = quoteCustomer ? primaryContact(quoteCustomer) : undefined;
  const setLine = (id: string, changes: Partial<QuoteLine>) => onChange({
    lines: quote.lines.map(line => line.id === id ? {
      ...line,
      ...changes
    } : line)
  });
  const addFromMaterial = (materialId: string) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) return;
    onChange({
      lines: [...quote.lines, {
        id: newLineId(),
        description: material.name,
        quantity: 1,
        unit: material.unit,
        unitPrice: material.salesPrice,
        discount: 0,
        materialId: material.id
      }]
    });
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500';
  if (preview) {
    const customer = customers.find(c => c.id === quote.customerId);
    return <div data-id="element-1895" data-mp="2ct0fqhj4fy3b">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden" data-id="element-1896" data-mp="2cpcjvbq7ezlf">
          <button onClick={() => setPreview(false)} className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1897" data-mp="27uqjs4pkotgn">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1898" data-mp="3ja5cuxenv16h" />
            Tilbage til redigering
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1899" data-mp="3rr60i28jeetq">
            <DownloadIcon className="h-4 w-4" data-id="element-1900" data-mp="3mqi0ws68spnw" />
            Gem som PDF
          </button>
        </div>

        <article className="mx-auto max-w-3xl overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1901" data-mp="eaf4jikkynev">
          <header className="flex flex-wrap items-start justify-between gap-6 border-b border-stone-200 p-8" data-id="element-1902" data-mp="j86va99ho3hh">
            <div data-id="element-1903" data-mp="4hzncnh3y2v1">
              <p className="text-xl font-bold tracking-tight text-stone-900" data-id="element-1904" data-mp="9j6uqlfc9016">
                {company.name}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-stone-500" data-id="element-1905" data-mp="x8hmjdepnk36">
                {company.address}
                <br data-id="element-1906" data-mp="120j6zmm18h4a" />
                {company.postalCode} {company.city}
                <br data-id="element-1907" data-mp="39isy6fel7noq" />
                CVR {company.cvr}
                <br data-id="element-1908" data-mp="39kdqdydfiujf" />
                {company.phone} · {company.email}
              </p>
            </div>
            <div className="text-right" data-id="element-1909" data-mp="35jk2v63wfhr2">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1910" data-mp="30huobghgwly8">
                Tilbud
              </p>
              <p className="font-mono text-lg font-bold text-stone-900" data-id="element-1911" data-mp="2vkl187hjwmdk">
                {quote.quoteNo}
              </p>
              <p className="mt-1 text-sm text-stone-500" data-id="element-1912" data-mp="3t94mlr7vmj1e">
                Dato {formatDate(quote.createdDate)}
                <br data-id="element-1913" data-mp="3oab9hzwi4u1r" />
                Gyldigt til {formatDate(quote.validUntil)}
              </p>
            </div>
          </header>

          <div className="border-b border-stone-200 p-8" data-id="element-1914" data-mp="3eink04ji9n2x">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1915" data-mp="3eg1d874fvblr">
              Til
            </p>
            <p className="mt-1 text-base font-semibold text-stone-900" data-id="element-1916" data-mp="b1ox7moojkkr">
              {quote.customerName}
            </p>
            {customer && <p className="text-sm text-stone-500" data-id="element-1917" data-mp="ie4or3qxrz0f">
                {customer.address}, {customer.postalCode} {customer.city}
              </p>}
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-stone-900" data-id="element-1918" data-mp="sa7zuz3rn7zi">
              {quote.title}
            </h1>
          </div>

          <table className="w-full text-sm" data-id="element-1919" data-mp="xb5hnakv600h">
            <thead data-id="element-1920" data-mp="11bpgqjozzdp9">
              <tr className="border-b border-stone-200 text-left" data-id="element-1921" data-mp="11aea8c463rug">
                <th className="px-8 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1922" data-mp="16aip5xpfgeik">
                  Beskrivelse
                </th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1923" data-mp="1b1r233pyui0h">
                  Antal
                </th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1924" data-mp="1l07w69k7bcqr">
                  Pris
                </th>
                <th className="px-8 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1925" data-mp="1oynq6xep9ldo">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-1926" data-mp="3h0qevm8op7gz">
              {quote.lines.map(line => <tr key={line.id} data-id="element-1927" data-mp="3uvswv7qt2na5">
                  <td className="px-8 py-2.5 text-stone-900" data-id="element-1928" data-mp="3q2znu7ob0ss9">
                    {line.description}
                    {line.discount > 0 && <span className="ml-2 text-xs text-emerald-600" data-id="element-1929" data-mp="8ouls57yr9pm">
                        −{line.discount}%
                      </span>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-1930" data-mp="hmwjobwehm8o">
                    {line.quantity} {line.unit}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-stone-600" data-id="element-1931" data-mp="2jfuda9y04iuo">
                    {krPrecise(line.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap px-8 py-2.5 text-right font-medium tabular-nums text-stone-900" data-id="element-1932" data-mp="258r4fcc47as3">
                    {krPrecise(lineTotal(line))}
                  </td>
                </tr>)}
            </tbody>
          </table>

          <div className="border-t border-stone-200 p-8" data-id="element-1933" data-mp="1vhdbjneo597x">
            <dl className="ml-auto max-w-xs space-y-1.5 text-sm" data-id="element-1934" data-mp="20hiumf6vhnee">
              <div className="flex justify-between" data-id="element-1935" data-mp="1lljoha3bpxbw">
                <dt className="text-stone-500" data-id="element-1936" data-mp="1ljqilx0jbh4y">Subtotal</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1937" data-mp="1qkwchy5xlag2">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-1938" data-mp="1cmg3g0klj7ur">
                <dt className="text-stone-500" data-id="element-1939" data-mp="1hj6mmwrp6jke">
                  Moms {Math.round(company.vatRate * 100)}%
                </dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1940" data-mp="36zxsjs0ngcrw">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-stone-200 pt-1.5 text-base font-bold" data-id="element-1941" data-mp="ocr77vhk4cze">
                <dt className="text-stone-900" data-id="element-1942" data-mp="jdo4ihp1uiul">Total inkl. moms</dt>
                <dd className="tabular-nums text-stone-900" data-id="element-1943" data-mp="jefunczvnodh">
                  {krPrecise(sums.total)}
                </dd>
              </div>
            </dl>

            <p className="mt-8 border-t border-stone-100 pt-5 text-xs leading-relaxed text-stone-500" data-id="element-1944" data-mp="am5ivr9c3au4">
              {company.quoteFooter}
            </p>
          </div>
        </article>
      </div>;
  }
  return <div data-id="element-1945" data-mp="menlncy8rsz">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1946" data-mp="lv7mc6v0dhl">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1947" data-mp="3nsxitejya0in" />
        Alle tilbud
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1948" data-mp="1q2e63v2gjtla">
        <div data-id="element-1949" data-mp="1m0sltonlgd12">
          <div className="flex items-center gap-2.5" data-id="element-1950" data-mp="1lzzkj37emfqb">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-1951" data-mp="1lyer5gwtt2u1">
              {quote.quoteNo}
            </p>
            <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-1952" data-mp="38x8vvvsm5a4u" />
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1953" data-mp="38v4jepgnf2sy">
            Tilbud
          </h1>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1954" data-mp="3hq2dhzvj8llv">
          <button onClick={() => setPreview(true)} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1955" data-mp="3horba4hqjlwv">
            <EyeIcon className="h-4 w-4" data-id="element-1956" data-mp="2tzov712r214h" />
            Forhåndsvis
          </button>
          {/* Reverting a sent quote to draft would rewrite history, so this
              only appears while it still is one. */}
          {quote.status === 'kladde' && <button onClick={saveDraft} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1957" data-mp="9c3ip1kar69y">
              {saved ? <>
                  <CheckIcon className="h-4 w-4 text-emerald-600" data-id="element-1958" data-mp="bip0879dto9d" />
                  Gemt
                </> : <>
                  <SaveIcon className="h-4 w-4" data-id="element-1959" data-mp="l9jjr3v8u9r4" />
                  Gem kladde
                </>}
            </button>}
          <button onClick={() => setSending(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1960" data-mp="3jutiofnewjst">
            <SendIcon className="h-4 w-4" data-id="element-1961" data-mp="1n40kfrsd02s" />
            Send tilbud
          </button>
        </div>
      </header>

      <SendDocumentDialog isOpen={sending} onClose={() => setSending(false)} kind="Tilbud" documentNo={quote.quoteNo} recipientName={quote.customerName} recipientEmail={quoteContact?.email || quoteCustomer?.email || ''} attention={quoteContact?.name} company={company} onSent={() => onChange({
      status: 'sendt',
      sentDate: toISODate(new Date())
    })} onOpenPreview={() => setPreview(true)} data-id="element-1962" data-mp="18zdy5pv7o6dh" />

      {quote.status === 'accepteret' && !quote.jobId && <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-id="element-1963" data-mp="2etk4gv4c4f4b">
          <p className="min-w-0 flex-1 text-sm text-emerald-900" data-id="element-1964" data-mp="29tz4chdfi0q0">
            Tilbuddet er accepteret. Opret opgaven, så linjerne følger med som
            forventede materialer.
          </p>
          <button onClick={() => onCreateJob(quote)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1965" data-mp="20u2e4bbe4tsu">
            <BriefcaseIcon className="h-4 w-4" data-id="element-1966" data-mp="3v8d10e8fddvz" />
            Opret opgave fra tilbud
          </button>
        </div>}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1967" data-mp="i4y3ziopp8ng">
        <div className="space-y-5 lg:col-span-2" data-id="element-1968" data-mp="mykilgefrmlw">
          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-1969" data-mp="96cpbymd2dry">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-1970" data-mp="949pynzbj8ak">
              <label className="block" data-id="element-1971" data-mp="e68uw2apid9m">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1972" data-mp="e33149r4un7k">
                  Kunde
                </span>
                <select value={quote.customerId} onChange={e => {
                const customer = customers.find(c => c.id === e.target.value);
                onChange({
                  customerId: e.target.value,
                  customerName: customer?.name ?? ''
                });
              }} className={field} data-id="element-1973" data-mp="15x55ew9ao5tq">
                  <option value="" data-id="element-1974" data-mp="3hjth9hgfigmz">Vælg kunde…</option>
                  {customers.map(customer => <option key={customer.id} value={customer.id} data-id="element-1975" data-mp="353k97ydekhgu">
                      {customer.customerNo} — {customer.name}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-1976" data-mp="3e40b53ehzgjl">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1977" data-mp="3e51m77vik31b">
                  Overskrift
                </span>
                <input value={quote.title} onChange={e => onChange({
                title: e.target.value
              })} placeholder="f.eks. Udskiftning af eltavle" className={field} data-id="element-1978" data-mp="2md2gvuwhfnw0" />
              </label>
              <label className="block" data-id="element-1979" data-mp="2vcfrws098kiu">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1980" data-mp="27no92r8k1eiq">
                  Status
                </span>
                <select value={quote.status} onChange={e => onChange({
                status: e.target.value as QuoteStatus
              })} className={field} data-id="element-1981" data-mp="22obmm7lli9go">
                  {(Object.keys(QUOTE_STATUS) as QuoteStatus[]).map(key => <option key={key} value={key} data-id="element-1982" data-mp="2q7wwlbgbhv06">
                      {QUOTE_STATUS[key].label}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-1983" data-mp="2z21w0j9ioerb">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1984" data-mp="3414pd6g4wwjg">
                  Gyldigt til
                </span>
                <input type="date" value={quote.validUntil} onChange={e => onChange({
                validUntil: e.target.value
              })} className={field} data-id="element-1985" data-mp="391sqiky2utre" />
              </label>
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1986" data-mp="1jkrpxot4y57v">
            <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-3.5" data-id="element-1987" data-mp="qoruntgi29b">
              <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1988" data-mp="3s41lahdd9gi2">
                Tilbudslinjer
              </h2>
              <label data-id="element-1989" data-mp="3o40ststfn4s0">
                <span className="sr-only" data-id="element-1990" data-mp="3o14xmc8yyu79">Hent fra materialekartotek</span>
                <select value="" onChange={e => addFromMaterial(e.target.value)} className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-medium outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1991" data-mp="3j5zefl6ybtmv">
                  <option value="" data-id="element-1992" data-mp="anbbxadykpaa">Hent fra kartotek…</option>
                  {materials.map(material => <option key={material.id} value={material.id} data-id="element-1993" data-mp="5p00iv80m6py">
                      {material.name}
                    </option>)}
                </select>
              </label>
              <button onClick={() => onChange({
              lines: [...quote.lines, emptyQuoteLine()]
            })} className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1994" data-mp="2qe5y7tqw785o">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-1995" data-mp="2iykgeu49tlsp" />
                Ny linje
              </button>
            </header>

            <div className="overflow-x-auto" data-id="element-1996" data-mp="296oh5fqg7pmw">
              <table className="w-full min-w-[640px] text-sm" data-id="element-1997" data-mp="294vbo3rknbgh">
                <thead data-id="element-1998" data-mp="2dxoc4fg0bxmr">
                  <tr className="border-b border-stone-100 bg-stone-50 text-left" data-id="element-1999" data-mp="2dxwrcdzlrf14">
                    {['Beskrivelse', 'Antal', 'Enhed', 'Stykpris', 'Rabat %', 'Total'].map(heading => <th key={heading} scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-2000" data-mp="1v6nhu4pu0q9j">
                        {heading}
                      </th>)}
                    <th className="w-10" data-id="element-2001" data-mp="1lirqdfs3lbvx" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100" data-id="element-2002" data-mp="1gjy9dddlr2r0">
                  {quote.lines.map(line => <tr key={line.id} data-id="element-2003" data-mp="1ujfzpx6jqo2h">
                      <td className="px-2 py-1" data-id="element-2004" data-mp="1ui4z268edwfl">
                        <input value={line.description} onChange={e => setLine(line.id, {
                      description: e.target.value
                    })} placeholder="Beskrivelse" aria-label="Beskrivelse" className={cell} data-id="element-2005" data-mp="1pkwejyzs1gb6" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-2006" data-mp="299nbju21t8u7">
                        <input type="number" value={line.quantity} onChange={e => setLine(line.id, {
                      quantity: parseFloat(e.target.value) || 0
                    })} aria-label="Antal" className={`${cell} text-right tabular-nums`} data-id="element-2007" data-mp="296sr42j1bo0s" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-2008" data-mp="31w6m7fvslmjf">
                        <input value={line.unit} onChange={e => setLine(line.id, {
                      unit: e.target.value
                    })} aria-label="Enhed" className={cell} data-id="element-2009" data-mp="31womqophmmx5" />
                      </td>
                      <td className="w-28 px-2 py-1" data-id="element-2010" data-mp="2i7nzr6268n8f">
                        <input type="number" value={line.unitPrice} onChange={e => setLine(line.id, {
                      unitPrice: parseFloat(e.target.value) || 0
                    })} aria-label="Stykpris" className={`${cell} text-right tabular-nums`} data-id="element-2011" data-mp="2i6cu10p615eo" />
                      </td>
                      <td className="w-20 px-2 py-1" data-id="element-2012" data-mp="1rrncxk0t13n8">
                        <input type="number" value={line.discount} onChange={e => setLine(line.id, {
                      discount: parseFloat(e.target.value) || 0
                    })} aria-label="Rabat i procent" className={`${cell} text-right tabular-nums`} data-id="element-2013" data-mp="1myanj7oawstf" />
                      </td>
                      <td className="whitespace-nowrap px-3 py-1 text-right font-semibold tabular-nums text-stone-900" data-id="element-2014" data-mp="12r3p3u5cy1hp">
                        {kr(lineTotal(line))}
                      </td>
                      <td className="px-1" data-id="element-2015" data-mp="ss4ei6117vs2">
                        <button onClick={() => onChange({
                      lines: quote.lines.filter(l => l.id !== line.id)
                    })} aria-label="Slet linje" className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-2016" data-mp="nuurex147w7e">
                          <Trash2Icon className="h-4 w-4" data-id="element-2017" data-mp="59v7cfyb2g6" />
                        </button>
                      </td>
                    </tr>)}
                  {quote.lines.length === 0 && <tr data-id="element-2018" data-mp="27pqiwegg3w6k">
                      <td colSpan={7} className="px-5 py-10 text-center text-sm text-stone-400" data-id="element-2019" data-mp="27qidpn27xpvs">
                        Ingen linjer endnu — tilføj en eller hent fra kartoteket.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-2020" data-mp="1o4m2pnylzq8a">
            <label className="block" data-id="element-2021" data-mp="1t2nmkb3uztkt">
              <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-2022" data-mp="1szj7giouvdfd">
                Bemærkninger til kunden
              </span>
              <textarea value={quote.notes} onChange={e => onChange({
              notes: e.target.value
            })} rows={3} className={field} data-id="element-2023" data-mp="22rytps76qo62" />
            </label>
          </section>
        </div>

        {/* Sums stay visible while you work, not buried at the bottom */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-2024" data-mp="128vji57rveln">
          <div className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-2025" data-mp="y9wfqnxs1nvs">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-2026" data-mp="1c08cj05b4at9">Sum</h2>
            <dl className="mt-4 space-y-2 text-sm" data-id="element-2027" data-mp="1c2m777w2rl0a">
              <div className="flex justify-between" data-id="element-2028" data-mp="172g7r5jkcu7n">
                <dt className="text-stone-500" data-id="element-2029" data-mp="173rene8b2i2z">Subtotal</dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-2030" data-mp="jclzz2e3pvu9">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between" data-id="element-2031" data-mp="t9qi8r4tt75s">
                <dt className="text-stone-500" data-id="element-2032" data-mp="tbbfwpmft67v">
                  Moms {Math.round(company.vatRate * 100)}%
                </dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-2033" data-mp="24p6vfzh97gzm">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
            </dl>
            <div className="mt-4 border-t border-stone-200 pt-4" data-id="element-2034" data-mp="1zpu9ri24cfym">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-2035" data-mp="3hmnvokprucet">
                Total inkl. moms
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-stone-900" data-id="element-2036" data-mp="3mkper5n76bqa">
                {krPrecise(sums.total)}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>;
}